import React, { useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Popover from '@mui/material/Popover';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import SearchIcon from '@mui/icons-material/Search';
import ConnectCardIcon from '@mui/icons-material/DescriptionOutlined';
import ConnectPointIcon from '@mui/icons-material/WebOutlined';
import NoteIcon from '@mui/icons-material/NoteOutlined';
import LinkIcon from '@mui/icons-material/Link';
import AddIcon from '@mui/icons-material/Add';
import { bindPopover } from 'material-ui-popup-state/hooks';
import TextField from 'components/form/TextInput';
import Spin from 'components/Spin';
import Button from 'components/Button';
import useAlert from 'hooks/useAlert';
import useLinkData from './useLinkData';
import NewPageModal from './NewPageModal';
import CustomizeUrl from 'modules/url-shortener/components/CustomizeUrl';
import { saveRedirect } from 'services/redirects.service';
import Access, { ACTIONS, MODULES } from 'components/Access';

const LINK_OPTIONS = [
  {
    label: 'Connect Cards',
    value: 'connect_card',
    Icon: ConnectCardIcon,
    color: '#F17676',
  },
  {
    label: 'Landing Page',
    value: 'connect_page',
    Icon: ConnectPointIcon,
    color: '#0CAED1',
  },
  {
    label: 'Pages',
    value: 'pages',
    Icon: NoteIcon,
    color: '#3e75ff',
  },
  {
    label: 'Shortened Links',
    value: 'short_url',
    Icon: LinkIcon,
    color: '#894FE8',
  },
];

const LinkAttachmentPopover = ({ popupState, onSelect }) => {
  const [type, setType] = useState(LINK_OPTIONS[0].value);
  const [query, setQuery] = useState('');
  const { data, loading, error } = useLinkData(
    popupState.isOpen && type,
    query,
  );

  const alert = useAlert();

  const handleItemClick = (item) => {
    onSelect(item);
    popupState.close();
  };

  const onPageSaved = (url) => {
    onSelect({ url });
    popupState.close();
  };

  const onToggleAddPage = () => {
    alert.dialog({
      render: ({ onCancel }) => (
        <NewPageModal onCancel={onCancel} onPageSaved={onPageSaved} />
      ),
      actions: [],
    });
  };

  const onToggleAddLink = () => {
    alert.dialog({
      dialogSize: 'xs',
      render: ({ onCancel, onConfirm }) => (
        <CustomizeUrl onClose={onCancel} onConfirm={onConfirm} />
      ),
      onConfirm: async (value) => {
        const data = {
          ...value,
          is_system_generated: '0',
          redir_url_short: value.prefix
            ? [value.prefix, value.redir_url_short].join('/')
            : value.redir_url_short,
        };
        const resp = await saveRedirect(data);
        handleItemClick({
          url: resp.qr_code_text,
        });
      },
      actions: [],
    });
  };

  const filteredData = useMemo(() => {
    if (data) {
      if (!query) return data;
      const ql = query.toLowerCase();
      return data.filter((item) => item.label.toLowerCase().includes(ql));
    }
    return [];
  }, [query, data]);

  const { Icon, color } = LINK_OPTIONS.find((item) => item.value === type);

  return (
    <Popover
      {...bindPopover(popupState)}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <TabContext value={type}>
        <TabList size="small" value={type} onChange={(e, v) => setType(v)}>
          {LINK_OPTIONS.map((i) => (
            <Tab key={i.value} value={i.value} label={i.label} />
          ))}
        </TabList>
        <Divider />
        <Box>
          {error && (
            <Box p={1}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}

          <List
            subheader={
              <Box px={2} py={1}>
                <TextField
                  fullWidth
                  margin="none"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  size="small"
                  endAdornment={<SearchIcon fontSize="small" color="inherit" />}
                  placeholder="Search..."
                />
                {type === 'pages' && (
                  <Access module={MODULES.VIDEO_NOTES} action={ACTIONS.MODIFY}>
                    <Box mt={2}>
                      <Button
                        startIcon={<AddIcon />}
                        variant="link"
                        onClick={onToggleAddPage}
                      >
                        Create New Page
                      </Button>
                    </Box>
                  </Access>
                )}
                {type === 'short_url' && (
                  <Box mt={2}>
                    <Button
                      startIcon={<AddIcon />}
                      variant="link"
                      onClick={onToggleAddLink}
                    >
                      Shorten New Link
                    </Button>
                  </Box>
                )}
              </Box>
            }
            disablePadding
            sx={{ height: 200, overflowY: 'auto' }}
          >
            <Spin spinning={loading}>
              {filteredData.map((item) => (
                <ListItem
                  button
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                >
                  <ListItemAvatar sx={{ minWidth: 40 }}>
                    <Avatar sx={{ width: 30, height: 30, bgcolor: color }}>
                      <Icon sx={{ fontSize: 16 }} />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={item.label || item.url} />
                </ListItem>
              ))}
              {data && !filteredData.length ? (
                <Typography color="text.secondary" textAlign="center">
                  No data
                </Typography>
              ) : null}
            </Spin>
          </List>
        </Box>
      </TabContext>
    </Popover>
  );
};

export default LinkAttachmentPopover;
